import { useCallback, useEffect, useRef, useState, type SyntheticEvent } from 'react';
import { HexColorPicker, HexColorInput } from 'react-colorful';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

const PRESET_COLORS = [
  '#EF4444',
  '#F97316',
  '#EAB308',
  '#22C55E',
  '#3B82F6',
  '#8B5CF6',
  '#EC4899',
  '#000000',
  '#6B7280',
  '#FFFFFF',
];

interface ColorPickerProps {
  color: string;
  onChange: (color: string) => void;
  label?: string;
  className?: string;
}

export function ColorPicker({ color, onChange, label, className }: ColorPickerProps) {
  // Handle transparent/empty values
  const normalizedColor = color && color !== 'none' && color !== 'transparent' ? color : '#FFFFFF';
  const isTransparent = !color || color === 'none' || color === 'transparent';
  const [draftColor, setDraftColor] = useState(normalizedColor);
  const draftColorRef = useRef(normalizedColor);
  const interactionStartColorRef = useRef(normalizedColor);
  const isInteractingRef = useRef(false);
  const commitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastCommittedColorRef = useRef(color);

  const clearCommitTimer = useCallback(() => {
    if (commitTimerRef.current) {
      clearTimeout(commitTimerRef.current);
      commitTimerRef.current = null;
    }
  }, []);

  const commitColor = useCallback(
    (nextColor: string) => {
      clearCommitTimer();

      if (lastCommittedColorRef.current === nextColor) return;
      lastCommittedColorRef.current = nextColor;
      onChange(nextColor);
    },
    [clearCommitTimer, onChange],
  );

  const scheduleCommit = useCallback(
    (nextColor: string) => {
      clearCommitTimer();
      commitTimerRef.current = setTimeout(() => {
        commitColor(nextColor);
      }, 350);
    },
    [clearCommitTimer, commitColor],
  );

  const handleDraftChange = useCallback((nextColor: string) => {
    draftColorRef.current = nextColor;
    setDraftColor(nextColor);
  }, []);

  const handlePickerChange = useCallback(
    (nextColor: string) => {
      handleDraftChange(nextColor);
    },
    [handleDraftChange],
  );

  const handleInputChange = useCallback(
    (nextColor: string) => {
      handleDraftChange(nextColor);
      scheduleCommit(nextColor);
    },
    [handleDraftChange, scheduleCommit],
  );

  const startInteraction = useCallback(() => {
    isInteractingRef.current = true;
    interactionStartColorRef.current = draftColorRef.current;
    clearCommitTimer();
  }, [clearCommitTimer]);

  const endInteraction = useCallback(() => {
    if (!isInteractingRef.current) return;
    isInteractingRef.current = false;
    if (draftColorRef.current === interactionStartColorRef.current) return;
    commitColor(draftColorRef.current);
  }, [commitColor]);

  const commitImmediate = useCallback(
    (nextColor: string) => {
      isInteractingRef.current = false;
      draftColorRef.current = nextColor;
      setDraftColor(nextColor);
      commitColor(nextColor);
    },
    [commitColor],
  );

  const stopToolbarPropagation = useCallback((event: SyntheticEvent) => {
    event.stopPropagation();
  }, []);

  useEffect(() => {
    lastCommittedColorRef.current = color;

    if (!isInteractingRef.current && !commitTimerRef.current) {
      draftColorRef.current = normalizedColor;
      setDraftColor(normalizedColor);
    }
  }, [color, normalizedColor]);

  useEffect(() => {
    document.addEventListener('pointerup', endInteraction);
    document.addEventListener('mouseup', endInteraction);
    document.addEventListener('touchend', endInteraction);
    window.addEventListener('blur', endInteraction);

    return () => {
      clearCommitTimer();
      document.removeEventListener('pointerup', endInteraction);
      document.removeEventListener('mouseup', endInteraction);
      document.removeEventListener('touchend', endInteraction);
      window.removeEventListener('blur', endInteraction);
    };
  }, [clearCommitTimer, endInteraction]);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className={cn(
            'w-7 h-7 rounded border-2 border-gray-200 hover:border-gray-400 transition-colors relative overflow-hidden',
            className,
          )}
          title={label || 'Pick color'}
          onMouseDown={stopToolbarPropagation}
          onPointerDown={stopToolbarPropagation}
        >
          {isTransparent ? (
            // Checkerboard pattern for transparent
            <div
              className="w-full h-full"
              style={{
                backgroundImage:
                  'linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)',
                backgroundSize: '8px 8px',
                backgroundPosition: '0 0, 0 4px, 4px -4px, -4px 0px',
              }}
            />
          ) : (
            <div className="w-full h-full rounded-sm" style={{ backgroundColor: draftColor }} />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-3"
        side="bottom"
        align="start"
        onMouseDown={stopToolbarPropagation}
        onPointerDown={stopToolbarPropagation}
        onTouchStart={stopToolbarPropagation}
      >
        {label && <p className="text-xs font-medium text-muted-foreground mb-2">{label}</p>}
        <div onPointerDown={startInteraction} onMouseDown={startInteraction}>
          <HexColorPicker color={draftColor} onChange={handlePickerChange} />
        </div>
        <div className="mt-3 flex items-center gap-2">
          <span className="text-xs text-muted-foreground">#</span>
          <HexColorInput
            color={draftColor}
            onChange={handleInputChange}
            onBlur={() => commitColor(draftColorRef.current)}
            className="w-20 text-sm border rounded px-2 py-1 uppercase"
            prefixed={false}
          />
        </div>
        <div className="mt-3 grid grid-cols-5 gap-1.5">
          {PRESET_COLORS.map(c => (
            <button
              key={c}
              className={cn(
                'w-6 h-6 rounded border hover:scale-110 transition-transform',
                c === '#FFFFFF' && 'border-gray-300',
                color === c && 'ring-2 ring-offset-1 ring-blue-500',
              )}
              style={{ backgroundColor: c }}
              onClick={() => commitImmediate(c)}
              title={c}
            />
          ))}
        </div>
        {/* Transparent option for fill colors */}
        <button
          className={cn(
            'mt-2 w-full text-xs text-muted-foreground hover:text-foreground py-1 rounded border border-dashed hover:border-solid transition-colors',
            isTransparent && 'border-blue-500 text-blue-600',
          )}
          onClick={() => commitImmediate('transparent')}
        >
          No fill (transparent)
        </button>
      </PopoverContent>
    </Popover>
  );
}
